import { useDynamicYieldApi } from '~/api/use-dynamic-yield-api';
import { useDynamicYieldCampaigns } from '~/composables/use-dynamic-yield-campaigns';
import { RECOMMENDATIONS_CAMPAIGNS, TEMPLATE_CAMPAIGNS } from '~/constants/campaigns';
import { SHOPIFY_TEMPLATE_TYPES } from '~/constants/shopify';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { trackClientSideCampaignViewed } from '~/lib/dynamic-yield/dynamic-yield-events';
import { isDynamicYieldEnabled } from '~/lib/dynamic-yield/dynamic-yield-settings';

/**
 * Automatically initializes Dynamic Yield campaigns based on the current template type.
 *
 * Template detection uses the class on the <main> element (e.g., 'template-product', 'template-index').
 */
class DynamicYieldAutoInit {
  constructor() {
    if (!isDynamicYieldEnabled()) {
      DataDogLogs.info('[Allbirds pixel] - Dynamic Yield - Disabled in theme settings');
      return;
    }

    this.initializeClientSideCampaignTracking();
    this.initializeOnDOMReady();
  }

  /**
   * Detects the current template type from the main element's class
   * @returns {string} The detected template type
   */
  detectTemplateType() {
    const mainElement = document.querySelector('main');
    if (!mainElement) {
      DataDogLogs.warn('[Allbirds pixel] - Dynamic Yield - No main element found');
      return '';
    }

    const classList = Array.from(mainElement.classList);
    const templateClass = classList.find((cls) => cls.startsWith('template-'));

    if (!templateClass) {
      DataDogLogs.warn('[Allbirds pixel] - Dynamic Yield - No template class found');
      return '';
    }

    // Extract template type from class (e.g., 'template-product' -> 'product')
    return templateClass.replace('template-', '');
  }

  /**
   * Initializes the appropriate DY campaigns based on the current template type
   */
  initializeCampaigns() {
    try {
      const { initializeCampaigns } = useDynamicYieldCampaigns();

      const detectedType = this.detectTemplateType();
      if (!detectedType) {
        DataDogLogs.error('[Allbirds pixel] - Dynamic Yield - Could not detect template type');
        return;
      }

      const templateType = this.mapTemplateType(detectedType);

      DataDogLogs.info('[Allbirds pixel] - Dynamic Yield - Initializing campaigns', {
        templateType,
      });

      // Configure campaigns to be loaded
      const campaignsToInitialize = [];

      // Add recommendation campaign for this template type (if available)
      const recommendationCampaign = RECOMMENDATIONS_CAMPAIGNS[templateType];
      if (recommendationCampaign) {
        campaignsToInitialize.push(recommendationCampaign);
      }

      // Add any template-specific campaigns (if available)
      const templateCampaigns = TEMPLATE_CAMPAIGNS[templateType];
      if (templateCampaigns?.length > 0) {
        campaignsToInitialize.push(...templateCampaigns);
      }

      // Filter out any undefined/null campaigns
      const filteredCampaigns = campaignsToInitialize.filter((campaign) => campaign);

      if (filteredCampaigns.length > 0) {
        initializeCampaigns(filteredCampaigns);
      } else {
        DataDogLogs.info('[Allbirds pixel] - Dynamic Yield - No campaigns for template', {
          templateType,
        });
      }
    } catch (error) {
      DataDogLogs.error('[Allbirds pixel] - Dynamic Yield - Error initializing', error);
    }
  }

  /**
   * Initialize the global ALLBIRDS.DynamicYield namespace and functions for Dynamic Yield connector
   * This makes functions available for DY's afterVariationRendered callback
   */
  initializeClientSideCampaignTracking() {
    window.ALLBIRDS = window.ALLBIRDS || {};
    window.ALLBIRDS.DynamicYield = {
      triggerExperimentViewed: trackClientSideCampaignViewed,
    };

    DataDogLogs.info(
      '[Allbirds pixel] - Dynamic Yield - Initialized window.ALLBIRDS.DynamicYield namespace',
    );
  }

  /**
   * Initialize DY campaigns when DOM is ready and DY scripts are loaded
   */
  initializeOnDOMReady() {
    document.addEventListener('DOMContentLoaded', () => {
      this.waitForDynamicYieldAndInit();
    });
  }

  /**
   * Maps template types to their corresponding SHOPIFY_TEMPLATE_TYPES constants
   * @param {string} templateType - The detected template type
   * @returns {string} The mapped template type constant
   */
  mapTemplateType(templateType) {
    return SHOPIFY_TEMPLATE_TYPES[templateType.toUpperCase()] || templateType;
  }

  /**
   * Waits for Dynamic Yield to be ready and then initializes campaigns
   * Uses setInterval pattern consistent with other integrations
   */
  waitForDynamicYieldAndInit() {
    const { isDynamicYieldInitialized } = useDynamicYieldApi();

    const MAX_ATTEMPTS = 60;
    const INTERVAL_MS = 1000;
    let attempts = 1;

    DataDogLogs.info('[Allbirds pixel] - Dynamic Yield - Checking initialization');

    const intervalID = setInterval(() => {
      if (isDynamicYieldInitialized()) {
        clearInterval(intervalID);
        DataDogLogs.info(`[Allbirds pixel] - Dynamic Yield - Ready after ${attempts} attempts`);
        this.initializeCampaigns();
      } else {
        if (attempts > MAX_ATTEMPTS) {
          clearInterval(intervalID);
          DataDogLogs.error(
            `[Allbirds pixel] - Dynamic Yield - Initialization timeout after ${attempts} attempts`,
            new Error('Dynamic Yield initialization timeout'),
          );
        }
        attempts++;
      }
    }, INTERVAL_MS);
  }
}

new DynamicYieldAutoInit();
